/**
 * @coeldery/lyrics-editor — **歌詞訊息內編輯**（撳句子高亮 → 逐句輸入）
 *
 * 血緣：cosingery（2026-09-22 產品負責人：「手動編輯 - after click on this button, can we highlight
 *   the phrase that need to change in the 歌詞訊息, after highlighted it, there will be an empty box to
 *   enter the text we want to change」；`decisions/026`）→ 依 R3 做模組。
 *
 * ⚠️ 模組**唔做**網絡：只交出「邊幾行高亮 + 每行打咗咩」；要 AI 改 or 直接套用由父層決定。
 */
import { useTranslation } from 'react-i18next';
import { splitLyricLines } from './lines.ts';

export { applyLineEdits, splitLyricLines } from './lines.ts';

export interface LyricsEditorProps {
  lyrics: string;
  /** 高亮咗嘅行 index（要改嗰幾句） */
  picked: number[];
  /** 每行打緊嘅字（index → text） */
  drafts: Record<number, string>;
  busy?: boolean;
  onToggleLine: (index: number) => void;
  onDraftChange: (index: number, value: string) => void;
  /** 提示（i18n key） */
  hintKey?: string;
  placeholderKey?: string;
}

export default function LyricsEditor({
  lyrics,
  picked,
  drafts,
  busy = false,
  onToggleLine,
  onDraftChange,
  hintKey,
  placeholderKey = 'edit_line_placeholder',
}: LyricsEditorProps) {
  const { t } = useTranslation();
  const lines = splitLyricLines(lyrics);

  return (
    <div data-testid="lyrics-editor" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {hintKey ? <p className="footnote">{t(hintKey)}</p> : null}
      {lines.map((line, i) => {
        const on = picked.includes(i);
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button
              type="button"
              data-testid="pick-line"
              aria-pressed={on}
              disabled={busy || line.trim().length === 0}
              onClick={() => onToggleLine(i)}
              style={{
                textAlign: 'left',
                padding: '1px 0',
                border: 'none',
                borderBottom: on ? '2px solid var(--color-primary)' : '1px dashed var(--color-divider)',
                background: on ? 'var(--color-primary-soft)' : 'transparent',
                color: 'var(--color-text)',
                font: 'inherit',
                cursor: line.trim().length === 0 ? 'default' : 'pointer',
              }}
            >
              {line.trim().length > 0 ? line : ' '}
            </button>
            {on ? (
              <input
                data-testid="line-draft"
                value={drafts[i] ?? ''}
                disabled={busy}
                placeholder={t(placeholderKey)}
                onChange={(e) => onDraftChange(i, e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1.5px solid var(--color-primary)',
                  borderRadius: '12px',
                  background: 'var(--color-card)',
                  color: 'var(--color-text)',
                  fontFamily: 'inherit',
                  fontSize: 'var(--font-size-small, 15px)',
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
